import React, { useState, useMemo, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Search, Check, ChevronDown, ShoppingCart, Plus, Minus, FileText, Loader, Tag, Layers, Clipboard } from "lucide-react";
import authAxios from "../../api/authAxios";
import { toast } from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import ProductDetailModal from "./ProductDetailModal";
import { resolveMediaUrl } from "../../utils/resolveMediaUrl";

export default function POSCatalogueModal({ isOpen, onClose, products = [], user, customers = [] }) {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showReview, setShowReview] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Cart state specifically for Catalogue
  const [catalogCart, setCatalogCart] = useState({}); // { productId: { product, qty } }
  
  // Image error state to handle broken urls or load failures gracefully
  const [imageErrors, setImageErrors] = useState({});

  // Premium Custom Dropdowns State
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
  const [customerDropdownOpen, setCustomerDropdownOpen] = useState(false);
  const [customerSearchQuery, setCustomerSearchQuery] = useState("");

  const categoryDropdownRef = useRef(null);
  const customerDropdownRef = useRef(null);

  // Detail Modal state for catalogue item custom selection (variants, weights, custom qty)
  const [selectedProductForDetail, setSelectedProductForDetail] = useState(null);

  const handleAddProductFromDetail = (item) => {
    // item is { ...product, qty, notes, price, variant_id, variant_name, cartItemId, stock }
    setCatalogCart(prev => {
      const key = item.cartItemId || item.id;
      
      const productCopy = {
        ...item,
        price: item.price,
        name: item.variant_name ? `${item.name} (${item.variant_name})` : item.name,
        id: item.id
      };

      return {
        ...prev,
        [key]: { 
          product: productCopy, 
          qty: parseFloat(item.qty) || 0,
          variant_name: item.variant_name,
          notes: item.notes,
          price: item.price
        }
      };
    });
    toast.success(`${item.name} added to request!`);
  };

  useEffect(() => {
    function handleClickOutside(event) {
      if (!event.target || !document.contains(event.target)) return;
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target)) {
        setCategoryDropdownOpen(false);
      }
      if (customerDropdownRef.current && !customerDropdownRef.current.contains(event.target)) {
        setCustomerDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Unique Categories
  const categories = useMemo(() => {
    const list = new Set(products.map(p => p.category).filter(Boolean));
    return ["All", ...Array.from(list)];
  }, [products]);

  // Filtered Products
  const filteredProducts = useMemo(() => {
    return products.filter(p => {
      const matchesSearch = p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                            (p.sku && p.sku.toLowerCase().includes(searchQuery.toLowerCase())) ||
                            (p.barcode && p.barcode.toLowerCase().includes(searchQuery.toLowerCase()));
      const matchesCategory = selectedCategory === "All" || p.category === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  }, [products, searchQuery, selectedCategory]);

  const handleUpdateQty = (product, delta) => {
    const key = product.cartItemId || product.id;
    setCatalogCart(prev => {
      const current = prev[key];
      if (!current) return prev;
      const currentQty = current.qty;
      const isWeight = product.product_type === 'weight_based';
      const step = isWeight ? 0.05 : 1;
      const nextQty = parseFloat((currentQty + delta * step).toFixed(2));

      if (nextQty <= 0) {
        const copy = { ...prev };
        delete copy[key];
        return copy;
      }

      // No stock limits for purchase requests

      return {
        ...prev,
        [key]: { 
          ...current,
          qty: nextQty 
        }
      };
    });
  };

  const cartItemsList = Object.values(catalogCart);
  const cartCount = cartItemsList.reduce((sum, item) => sum + (parseFloat(item.qty) || 0), 0);
  const subtotal = cartItemsList.reduce((sum, item) => sum + ((parseFloat(item.price || item.product.price || 0)) * (parseFloat(item.qty) || 0)), 0);

  // Generate real unique purchase request invoice number
  const generatePRNumber = () => {
    const date = new Date().toISOString().slice(0, 10).replace(/-/g, "");
    const random = Math.floor(1000 + Math.random() * 9000);
    return `PR-${date}-${random}`;
  };

  const handleGenerateInvoice = async () => {
    if (cartItemsList.length === 0) {
      toast.error("Please select at least one product.");
      return;
    }

    setLoading(true);
    try {
      const prNo = generatePRNumber();
      const taxRateValue = 18; // Default standard GST
      const taxAmountValue = parseFloat((subtotal * (taxRateValue / 100)).toFixed(2));
      const cgstVal = parseFloat((taxAmountValue / 2).toFixed(2));
      const sgstVal = parseFloat((taxAmountValue / 2).toFixed(2));
      const totalAmt = parseFloat((subtotal + taxAmountValue).toFixed(2));

      // Match the customer billing data format
      const formattedItems = cartItemsList.map(item => ({
        id: item.product.id,
        name: item.product.name,
        qty: parseFloat(item.qty) || 0,
        price: parseFloat(item.price || item.product.price || 0),
        discount: 0,
        tax_rate: parseFloat(item.product.tax_rate || taxRateValue),
        variant_name: item.variant_name || "",
        unit: item.product.unit || (item.product.product_type === "weight_based" ? "kg" : "units")
      }));

      const purchaseRequestData = {
        invoice_number: prNo,
        customer: selectedCustomer?.id || null,
        total_amount: totalAmt,
        status: "pending",
        invoice_data: {
          invoiceNo: prNo,
          totalAmount: totalAmt,
          subtotal: parseFloat(subtotal.toFixed(2)),
          taxAmount: taxAmountValue,
          cgst: cgstVal,
          sgst: sgstVal,
          igst: 0,
          taxRate: taxRateValue,
          items: formattedItems,
          customer: selectedCustomer,
          billingType: "Purchase",
          billingMode: "with_gst",
          billerName: `${user?.first_name || ""} ${user?.last_name || ""}`.trim() || user?.phone,
          billerId: user?.salesman_id || "Sales Staff",
          isPurchaseInvoice: true
        }
      };

      // Save Purchase Request in database
      const res = await authAxios.post("/api/purchase/requests/", purchaseRequestData);

      toast.success("Purchase Request generated successfully!");

      // Open Invoice in SAME pattern as Customer Billing
      onClose();
      localStorage.setItem("latest_purchase_request", JSON.stringify(purchaseRequestData.invoice_data));
      navigate("/pos/invoice-success", { state: { ...purchaseRequestData.invoice_data, isPurchaseInvoice: true } });

    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || "Failed to generate Purchase Invoice.");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-900/40 backdrop-blur-md p-4 md:p-8">
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 10 }}
        className="bg-white border border-slate-200 rounded-3xl w-full max-w-6xl h-[90vh] flex flex-col overflow-hidden shadow-2xl"
      >
        {/* Header */}
        <div className="p-6 border-b border-slate-200 flex items-center justify-between bg-slate-50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-600 flex items-center justify-center">
              <Layers className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-xl font-extrabold text-slate-800">Product Catalogue</h2>
              <p className="text-xs text-slate-500">Select products and generate purchase requests</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500 hover:text-slate-800 flex items-center justify-center transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Search, Filter and Customer Selection Bar */}
        <div className="p-4 bg-slate-50 border-b border-slate-200 flex flex-col md:flex-row gap-4 items-center justify-between">
          <div className="relative w-full md:w-96">
            <Search className="absolute left-3.5 top-3 w-5 h-5 text-slate-400" />
            <input
              type="text"
              placeholder="Search by name, code, sku..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-11 pr-4 py-2.5 bg-white border border-slate-200 rounded-2xl text-slate-800 placeholder-slate-400 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition shadow-sm"
            />
          </div>

          <div className="flex flex-wrap items-center gap-3 w-full md:w-auto relative">
            {/* Custom Category Dropdown */}
            <div className="relative z-[95]" ref={categoryDropdownRef}>
              <button
                type="button"
                onClick={() => {
                  setCategoryDropdownOpen(!categoryDropdownOpen);
                  setCustomerDropdownOpen(false);
                }}
                className="flex items-center justify-between gap-2.5 px-4 py-2.5 bg-white border border-slate-200 text-slate-700 rounded-2xl hover:border-slate-300 hover:bg-slate-50 text-sm font-bold shadow-sm transition-all focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 min-w-[160px]"
              >
                <span>{selectedCategory === "All" ? "All Categories" : selectedCategory}</span>
                <ChevronDown className={`w-4 h-4 transition-transform text-slate-400 ${categoryDropdownOpen ? "rotate-180" : ""}`} />
              </button>

              <AnimatePresence>
                {categoryDropdownOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    transition={{ duration: 0.12 }}
                    className="absolute left-0 mt-2 w-56 bg-white border border-slate-200 rounded-2xl shadow-xl z-[99] overflow-hidden py-1"
                  >
                    {categories.map(c => (
                      <button
                        key={c}
                        type="button"
                        onClick={() => {
                          setSelectedCategory(c);
                          setCategoryDropdownOpen(false);
                        }}
                        className={`w-full text-left px-4 py-2.5 text-sm transition-all flex items-center justify-between
                          ${selectedCategory === c 
                            ? "bg-emerald-50 text-emerald-600 font-extrabold" 
                            : "text-slate-600 hover:bg-slate-50 hover:text-slate-900 font-medium"
                          }`}
                      >
                        <span>{c === "All" ? "All Categories" : c}</span>
                        {selectedCategory === c && <Check className="w-4 h-4 text-emerald-500" />}
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Custom Customer Selection Dropdown with Search */}
            <div className="relative z-[95]" ref={customerDropdownRef}>
              <button
                type="button"
                onClick={() => {
                  setCustomerDropdownOpen(!customerDropdownOpen);
                  setCategoryDropdownOpen(false);
                  setCustomerSearchQuery("");
                }}
                className="flex items-center justify-between gap-3 px-4 py-1.5 bg-white border border-slate-200 text-slate-700 rounded-2xl hover:border-slate-300 hover:bg-slate-50 text-sm font-bold shadow-sm transition-all focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 w-full md:w-64 min-h-[46px]"
              >
                <div className="flex flex-col items-start text-left truncate">
                  <span className="text-[9px] text-slate-400 uppercase tracking-widest font-black -mb-0.5">Billed Customer</span>
                  <span className="text-xs truncate font-extrabold text-slate-800">
                    {selectedCustomer ? `${selectedCustomer.name}` : "Select Customer (Optional)"}
                  </span>
                </div>
                <ChevronDown className={`w-4 h-4 transition-transform text-slate-400 ${customerDropdownOpen ? "rotate-180" : ""}`} />
              </button>

              <AnimatePresence>
                {customerDropdownOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    transition={{ duration: 0.12 }}
                    className="absolute right-0 mt-2 w-72 bg-white border border-slate-200 rounded-2xl shadow-xl z-[99] overflow-hidden flex flex-col max-h-80"
                  >
                    {/* Search Field inside Dropdown */}
                    <div className="p-2.5 border-b border-slate-100 bg-slate-50">
                      <div className="relative">
                        <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-slate-400" />
                        <input
                          type="text"
                          placeholder="Search customer by name/phone..."
                          value={customerSearchQuery}
                          onChange={(e) => setCustomerSearchQuery(e.target.value)}
                          onClick={(e) => e.stopPropagation()} // Prevent closing dropdown on input click
                          className="w-full pl-8 pr-3 py-1.5 bg-white border border-slate-200 rounded-xl text-xs font-semibold focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 text-slate-800 placeholder-slate-400"
                        />
                      </div>
                    </div>

                    {/* Customer Options List */}
                    <div className="overflow-y-auto py-1 flex-1">
                      {/* Walk-in selection */}
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedCustomer(null);
                          setCustomerDropdownOpen(false);
                        }}
                        className={`w-full text-left px-4 py-2 text-xs transition-all flex flex-col
                          ${!selectedCustomer 
                            ? "bg-emerald-50 text-emerald-600 font-extrabold" 
                            : "text-slate-600 hover:bg-slate-50 hover:text-slate-900 font-medium"
                          }`}
                      >
                        <span className="font-extrabold">Walk-In Customer</span>
                        <span className="text-[10px] text-slate-400">No profile recorded</span>
                      </button>

                      <div className="border-t border-slate-100 my-1"></div>

                      {/* Filtered Customers list */}
                      {customers
                        .filter(c => {
                          const query = customerSearchQuery.toLowerCase();
                          return (
                            c.name.toLowerCase().includes(query) ||
                            c.phone.includes(query)
                          );
                        })
                        .map(c => {
                          const isSelected = selectedCustomer?.id === c.id;
                          return (
                            <button
                              key={c.id}
                              type="button"
                              onClick={() => {
                                setSelectedCustomer(c);
                                setCustomerDropdownOpen(false);
                              }}
                              className={`w-full text-left px-4 py-2 text-xs transition-all flex flex-col
                                ${isSelected 
                                  ? "bg-emerald-50 text-emerald-600 font-extrabold" 
                                  : "text-slate-600 hover:bg-slate-50 hover:text-slate-900 font-medium"
                                }`}
                            >
                              <span className="font-bold">{c.name}</span>
                              <span className="text-[10px] text-slate-400">{c.phone}</span>
                            </button>
                          );
                        })}

                      {/* Empty search fallback */}
                      {customers.filter(c => {
                        const query = customerSearchQuery.toLowerCase();
                        return c.name.toLowerCase().includes(query) || c.phone.includes(query);
                      }).length === 0 && (
                        <div className="px-4 py-4 text-center text-xs font-extrabold text-slate-400">
                          No matching customers
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-hidden flex flex-col lg:flex-row bg-slate-50/40">
          {/* Product Grid */}
          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
              {filteredProducts.map(product => {
                const productCartItems = Object.values(catalogCart).filter(item => item.product.id === product.id);
                const selectedQty = productCartItems.reduce((sum, item) => sum + item.qty, 0);

                return (
                  <div
                    key={product.id}
                    onClick={() => setSelectedProductForDetail(product)}
                    className={`relative p-5 rounded-2xl border bg-white transition duration-300 flex flex-col justify-between cursor-pointer ${
                      selectedQty > 0 ? "border-emerald-500 shadow-lg shadow-emerald-500/10" : "border-slate-200 hover:border-slate-300 hover:shadow-sm"
                    }`}
                  >
                    {/* Badge */}
                    {product.stock <= 0 ? (
                      <span className="absolute top-4 right-4 px-2.5 py-1 text-[10px] font-black uppercase tracking-wider bg-red-50 text-red-600 border border-red-100 rounded-full">
                        Out of Stock
                      </span>
                    ) : product.stock <= 5 ? (
                      <span className="absolute top-4 right-4 px-2.5 py-1 text-[10px] font-black uppercase tracking-wider bg-amber-50 text-amber-600 border border-amber-100 rounded-full">
                        Low Stock: {product.stock}
                      </span>
                    ) : (
                      <span className="absolute top-4 right-4 px-2.5 py-1 text-[10px] font-black uppercase tracking-wider bg-emerald-50 text-emerald-600 border border-emerald-100 rounded-full">
                        Stock: {product.stock}
                      </span>
                    )}

                    <div>
                      {/* Product Image Fallback */}
                      <div className="w-full h-32 rounded-xl bg-slate-100 flex items-center justify-center overflow-hidden mb-4 relative border border-slate-200/50">
                        {product.image && !imageErrors[product.id] ? (
                          <img 
                            src={resolveMediaUrl(product.image)} 
                            alt={product.name} 
                            className="w-full h-full object-cover" 
                            onError={() => setImageErrors(prev => ({ ...prev, [product.id]: true }))}
                          />
                        ) : (
                          <div className="flex flex-col items-center justify-center text-slate-400 gap-1">
                            <Tag className="w-8 h-8 opacity-60 text-emerald-600/60" />
                            <span className="text-[10px] uppercase font-bold tracking-widest text-slate-500">{product.category || "General"}</span>
                          </div>
                        )}
                      </div>

                      <h4 className="font-extrabold text-slate-800 text-base leading-snug line-clamp-1">{product.name}</h4>
                      <p className="text-xs text-slate-400 mt-1 uppercase tracking-wider">{product.category || "No Category"}</p>
                      
                      <div className="mt-3 flex items-baseline gap-1.5">
                        <span className="text-xl font-black text-emerald-600">₹{product.price}</span>
                        <span className="text-xs text-slate-400 font-medium">/ unit</span>
                      </div>
                    </div>

                    {/* Quantity Selector / Add Button */}
                    <div className="mt-5" onClick={(e) => e.stopPropagation()}>
                      {selectedQty > 0 ? (
                        <button
                          onClick={() => setSelectedProductForDetail(product)}
                          className="w-full py-2.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-750 font-black rounded-xl transition border-2 border-emerald-500/30 flex items-center justify-center gap-2 text-sm shadow-sm"
                        >
                          <Check className="w-4 h-4 text-emerald-600" />
                          <span>{selectedQty} Selected</span>
                        </button>
                      ) : (
                        <button
                          onClick={() => setSelectedProductForDetail(product)}
                          className="w-full py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white font-extrabold rounded-xl transition flex items-center justify-center gap-2 text-sm shadow-md shadow-emerald-600/10"
                        >
                          <Plus className="w-4 h-4" />
                          Add to Request
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}

              {filteredProducts.length === 0 && (
                <div className="col-span-full py-20 flex flex-col items-center justify-center text-slate-450">
                  <Search className="w-12 h-12 opacity-30 mb-4 text-slate-400" />
                  <p className="font-extrabold text-lg text-slate-600">No catalogue products match your search</p>
                  <p className="text-sm text-slate-400 mt-1">Try resetting the filters or typing a different keyword</p>
                </div>
              )}
            </div>
          </div>

          {/* Selected items review panel (Sidebar on large screen) */}
          <div className="w-full lg:w-96 border-t lg:border-t-0 lg:border-l border-slate-200 bg-white flex flex-col justify-between shadow-lg">
            <div className="p-6 overflow-y-auto flex-1 space-y-4">
              <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                <h3 className="font-extrabold text-slate-800 text-lg flex items-center gap-2">
                  <Clipboard className="w-5 h-5 text-emerald-600" />
                  Request Summary
                </h3>
                <span className="px-3 py-1 bg-emerald-50 text-emerald-700 font-extrabold text-xs rounded-full border border-emerald-100">
                  {cartCount} Items
                </span>
              </div>

              {cartItemsList.length === 0 ? (
                <div className="py-20 text-center text-slate-400 flex flex-col items-center">
                  <ShoppingCart className="w-10 h-10 opacity-30 mb-3 text-slate-500" />
                  <p className="text-sm font-semibold text-slate-600">Catalogue selection is empty.</p>
                  <p className="text-xs text-slate-400 mt-1">Add items from the left grid.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {cartItemsList.map(({ product, qty, variant_name, notes, price }) => (
                    <div key={product.cartItemId || product.id} className="p-3 bg-slate-50 border border-slate-150 rounded-xl flex items-center justify-between gap-4 shadow-sm">
                      <div className="flex-1">
                        <p className="text-slate-800 text-sm font-bold line-clamp-1">{product.name}</p>
                        {variant_name && <p className="text-[10px] text-emerald-600 font-extrabold uppercase tracking-wider mt-0.5">{variant_name}</p>}
                        {notes && <p className="text-[10px] text-slate-400 italic line-clamp-1 mt-0.5">Note: {notes}</p>}
                        <p className="text-xs text-slate-500 mt-0.5">{qty} x ₹{price || product.price}</p>
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleUpdateQty(product, -1)}
                          className="p-1 hover:bg-slate-200 text-slate-500 hover:text-slate-800 rounded transition"
                        >
                          <Minus className="w-3.5 h-3.5" />
                        </button>
                        <span className="text-slate-800 text-sm font-bold px-1.5">{qty}</span>
                        <button
                          onClick={() => handleUpdateQty(product, 1)}
                          className="p-1 hover:bg-slate-200 text-slate-500 hover:text-slate-800 rounded transition"
                        >
                          <Plus className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Price Calculations and CTA */}
            <div className="p-6 border-t border-slate-200 bg-slate-50 space-y-4">
              <div className="space-y-2 text-sm text-slate-600">
                <div className="flex justify-between">
                  <span>Subtotal:</span>
                  <span className="font-bold text-slate-800">₹{subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Est. GST (18%):</span>
                  <span className="font-bold text-slate-800">₹{(subtotal * 0.18).toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-base font-extrabold text-slate-800 border-t border-slate-200 pt-3">
                  <span className="text-emerald-600">GRAND TOTAL:</span>
                  <span className="text-emerald-600 text-lg">₹{(subtotal * 1.18).toFixed(2)}</span>
                </div>
              </div>

              <button
                onClick={handleGenerateInvoice}
                disabled={cartItemsList.length === 0 || loading}
                className="w-full py-3.5 bg-gradient-to-r from-emerald-600 via-emerald-500 to-teal-600 hover:opacity-95 text-white font-black rounded-2xl flex items-center justify-center gap-2 shadow-lg shadow-emerald-500/20 active:scale-[0.99] transition disabled:opacity-45 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <Loader className="w-5 h-5 animate-spin" />
                ) : (
                  <FileText className="w-5 h-5" />
                )}
                <span>Generate Purchase Invoice</span>
              </button>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Premium Product Detail Modal for custom weights, variants, quantities, and instructions */}
      <ProductDetailModal
        isOpen={!!selectedProductForDetail}
        onClose={() => setSelectedProductForDetail(null)}
        product={selectedProductForDetail}
        onAddToCart={handleAddProductFromDetail}
      />
    </div>
  );
}
